import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';

const Register = () => {
  const navigate = useNavigate();
  const { register, error } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setNameError('');
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    
    // Validate fields
    let isValid = true;
    
    if (!name) {
      setNameError('Name is required');
      isValid = false;
    }
    
    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }
    
    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      isValid = false;
    }
    
    if (!confirmPassword) {
      setConfirmPasswordError('Please confirm your password');
      isValid = false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      isValid = false;
    }
    
    if (isValid) {
      setIsLoading(true);
      try {
        const result = await register(email, password, name);
        if (result.success) {
          if (result.requiresVerification) {
            // Show verification message instead of navigating
            setRegistrationComplete(true);
            setVerificationMessage(result.message || 'Please check your email to verify your account.');
          } else {
            // Direct login (legacy flow)
            navigate('/');
          }
        }
      } catch (err) {
        console.error('Registration error:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <style>{`
        .register-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%);
          position: relative;
          overflow: hidden;
          padding: 40px 0;
        }
        .register-page::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 50%);
          animation: pulse 15s ease-in-out infinite;
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        .register-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
        }
        .register-title {
          font-size: 2.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .register-input,
        .register-input:focus,
        .register-input:active,
        .register-page .form-control {
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 12px !important;
          color: #ffffff !important;
          padding: 12px 16px !important;
          transition: all 0.3s ease;
          -webkit-text-fill-color: #ffffff !important;
        }
        .register-input:focus {
          background: rgba(255, 255, 255, 0.08) !important;
          border-color: rgba(102, 126, 234, 0.5) !important;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
        }
        .register-input::placeholder {
          color: rgba(255, 255, 255, 0.4) !important;
        }
        .register-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 12px;
          padding: 14px 20px;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
        }
        .register-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
        }
        .register-btn:disabled {
          opacity: 0.7;
          transform: none;
        }
      `}</style>
      
      <div className="register-page">
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} sm={10} md={8} lg={5} xl={4}>
              <div>
                <div className="text-center mb-4">
                  <Link to="/welcome" style={{ textDecoration: 'none' }}>
                    <h1 className="register-title">
                      <span role="img" aria-label="logo">📊</span> EchoBrief
                    </h1>
                  </Link>
                  <p style={{ color: 'rgba(255,255,255,0.6)' }}>Create a new account</p>
                </div>
                
                <Card className="register-card border-0">
                  <Card.Body className="p-4 p-md-5">
                    {registrationComplete ? (
                      <div className="text-center">
                        <div className="mb-4">
                          <span style={{ fontSize: '4rem' }} role="img" aria-label="email">📧</span>
                        </div>
                        <h4 className="mb-3" style={{ color: 'white' }}>Check your email!</h4>
                        <Alert variant="success">
                          {verificationMessage}
                        </Alert>
                        <p style={{ color: 'rgba(255,255,255,0.6)' }} className="mb-4">
                          We've sent a verification link to <strong style={{ color: 'white' }}>{email}</strong>. 
                          Click the link in the email to activate your account.
                        </p>
                        <div className="d-grid gap-2">
                          <Link to="/login">
                            <Button className="register-btn w-100">
                              Go to Login
                            </Button>
                          </Link>
                          <Button 
                            variant="outline-light" 
                            onClick={() => {
                              setRegistrationComplete(false);
                              setVerificationMessage('');
                            }}
                            style={{ borderRadius: '12px' }}
                          >
                            Use different email
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {error && (
                          <Alert variant="danger" className="mb-4">
                            {error}
                          </Alert>
                        )}
                        <Form onSubmit={handleRegister}>
                          <Form.Group className="mb-3">
                            <Form.Label style={{ color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>Full Name</Form.Label>
                            <Form.Control
                              type="text"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              isInvalid={!!nameError}
                              placeholder="Enter your full name"
                              className="register-input"
                            />
                            {nameError && (
                              <Form.Control.Feedback type="invalid">
                                {nameError}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label style={{ color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>Email Address</Form.Label>
                            <Form.Control
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              isInvalid={!!emailError}
                              placeholder="Enter your email"
                              className="register-input"
                            />
                            {emailError && (
                              <Form.Control.Feedback type="invalid">
                                {emailError}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label style={{ color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>Password</Form.Label>
                            <Form.Control
                              type={showPassword ? 'text' : 'password'}
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              isInvalid={!!passwordError}
                              placeholder="Create a password (min 8 characters)"
                              className="register-input"
                            />
                            {passwordError && (
                              <Form.Control.Feedback type="invalid">
                                {passwordError}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                          
                          <Form.Group className="mb-4">
                            <Form.Label style={{ color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>Confirm Password</Form.Label>
                            <Form.Control
                              type={showPassword ? 'text' : 'password'}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              isInvalid={!!confirmPasswordError}
                              placeholder="Confirm your password"
                              className="register-input"
                            />
                            {confirmPasswordError && (
                              <Form.Control.Feedback type="invalid">
                                {confirmPasswordError}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                          
                          <Form.Check 
                            type="checkbox" 
                            label="Show passwords" 
                            onChange={handleTogglePasswordVisibility}
                            checked={showPassword}
                            className="mb-4"
                            style={{ color: 'rgba(255,255,255,0.6)' }}
                          />
                          
                          <Button
                            type="submit"
                            className="register-btn w-100 mb-4"
                            disabled={isLoading}
                          >
                            {isLoading ? 'Creating Account...' : 'Create Account'}
                          </Button>
                          
                          <div className="text-center">
                            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 0 }}>
                              Already have an account?{' '}
                              <Link to="/login" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '500' }}>
                                Sign In
                              </Link>
                            </p>
                          </div>
                        </Form>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Register;
